import React from 'react'
import { Flex, LoadingSpinner, Text } from '@sendoutcards/quantum-design-ui'
import { SelectableGiftCardType } from './GiftCard'
import { useDigitalGiftCardProducts, useGlobalSettings } from 'src/react_query'
import { useState } from 'src/hooks'
import { DigitalGiftCardProduct } from 'src/graphql/generated/graphql'
import { GiftCardPreview } from './GiftCardPreview'
import ActSetupSubActionHeader from '../ActSetupPanel/ActSetupSubActionHeader'
import CountrySelect from './CountrySelect'
import { AnimatePresence, motion } from 'motion/react'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'

const INNER_ACTION_VARIANTS = {
  enter: {
    opacity: 1,
    x: '0%',
    y: '0%',
    transition: {
      duration: 0.2,
      delay: 0.1,
    },
  },
  exit: {
    opacity: 1,
    x: '100%',
    y: '0%',
    transition: {
      duration: 0.1,
    },
  },
  exitDown: {
    opacity: 0,
    x: '0%',
    y: '100%',
    transition: {
      duration: 0.1,
    },
  },
}

type GiftCardCatalogProps = {
  currentlyAddedGiftCard?: SelectableGiftCardType
  onSelect: (giftCard: SelectableGiftCardType) => Promise<void>
  onRemove: (giftCard: SelectableGiftCardType) => void
  setMobileSheetHeight: (sheetHeight: string | undefined) => void
  onBack: () => void
}

const GiftCardCatalog: React.FC<GiftCardCatalogProps> = ({
  currentlyAddedGiftCard,
  onSelect,
  onRemove,
  setMobileSheetHeight,
  onBack,
}) => {
  const globalSettings = useGlobalSettings()
  const isAddingGiftCardsDisabled =
    globalSettings.data?.digitalGiftCardAddDisabled

  const [selectedCountryCode, setSelectedCountryCode] = useState(
    currentlyAddedGiftCard ? currentlyAddedGiftCard.code.split('-')[1] : 'US',
  )
  const [selectedGiftCard, setSelectedGiftCard] = useState<
    DigitalGiftCardProduct | undefined
  >(undefined)

  // NOTE: We will likely use this when applying the new designs
  // const digitalCardProductsQuery = useInfiniteDigitalCardProducts(
  //   {
  //     countryRedeemableIn: selectedCountryCode,
  //     limit: 20,
  //   },
  //   true,
  // )
  // const giftCards =
  //   digitalCardProductsQuery.data?.pages
  //     .flatMap(page => page.results)
  //     .filter(value => value !== null && value !== undefined)
  //     .slice(0, 4) ?? []

  const productCodes = [
    'BATHBW', // Bath & Body Works
    selectedCountryCode === 'CA' ? 'THEGAP' : 'GAP', // Gap
    // selectedCountryCode === 'CA' ? 'VITVS12' : 'VISMA', // Visa
    'SBUX', // Starbucks
    // selectedCountryCode === 'CA' ? 'SEPH' : 'SEPHRA', // Sephora
    ...(selectedCountryCode === 'US' ? ['BARNES'] : []), // Barnes & Noble
    ...(selectedCountryCode === 'US' ? ['GRBHUB'] : []), // GrubHub
    ...(selectedCountryCode === 'US' ? ['LOWES'] : []), // Lowed
    // selectedCountryCode === 'CA' ? 'MSTR12' : 'VITMA12', // Mastercard
    selectedCountryCode === 'CA' ? 'BASSP' : 'BASPRO', // Bass Pro
    ...(selectedCountryCode === 'US' ? ['DUNKIN'] : []), // Dunkin Donuts
    ...(selectedCountryCode === 'CA' ? ['TIMHOR'] : []), // Tim Hortons
  ]
  const productQueries = useDigitalGiftCardProducts(
    productCodes.map(code => `${code}-${selectedCountryCode}`),
  )

  const isFetchingProducts = productQueries.some(query => query.isFetching)

  const availableGiftCards = productQueries.flatMap(query =>
    query.data ? [query.data] : [],
  )

  return (
    <>
      <div>
        <ActSetupSubActionHeader
          title={
            selectedGiftCard ? selectedGiftCard.title : 'Digital Gift Cards'
          }
          isFixedHeader={true}
          onBack={
            selectedGiftCard
              ? () => {
                  setMobileSheetHeight(undefined)
                  setSelectedGiftCard(undefined)
                }
              : onBack
          }
        >
          <CountrySelect
            selectedValue={selectedCountryCode}
            onSelect={setSelectedCountryCode}
          />
        </ActSetupSubActionHeader>
      </div>
      <AnimatePresence>
        {selectedGiftCard ? (
          <motion.div
            key="giftCardPreview"
            variants={INNER_ACTION_VARIANTS}
            initial="exit"
            exit="exit"
            animate="enter"
            style={{
              padding: '64px 0',
              overflow: 'auto',
              maxHeight: '80dvh',
            }}
            // Workaround as support for inert was only added in React 19
            {...{ inert: isAddingGiftCardsDisabled ? '' : undefined }}
          >
            <GiftCardPreview
              selectedGiftCard={selectedGiftCard}
              onSelect={async (giftCard: SelectableGiftCardType) => {
                await onSelect({
                  code: giftCard.code,
                  title: giftCard.title,
                  imageUrl: giftCard.imageUrl,
                  amount: giftCard.amount,
                  currency: giftCard.currency,
                })
              }}
            />
          </motion.div>
        ) : (
          <>
            {isFetchingProducts || globalSettings.isLoading ? (
              <div style={{ paddingTop: '5.5rem' }}>
                <LoadingSpinner size="large" />
                <Text
                  type="body"
                  content="Loading Gift Cards..."
                  alignment="center"
                  style={{ marginTop: '2.5rem' }}
                />
              </div>
            ) : (
              <motion.div
                key="giftCardCatalog"
                initial="exitDown"
                exit="exitDown"
                animate="enter"
                variants={INNER_ACTION_VARIANTS}
                style={{
                  padding: '78px 16px 16px',
                  display: 'grid',
                  gridTemplateColumns: 'minmax(166px, 1fr) minmax(166px, 1fr)',
                  rowGap: '0.5rem',
                  columnGap: '0.5rem',
                  maxHeight: '80dvh',
                  overflow: 'auto',
                }}
                // Workaround as support for inert was only added in React 19
                {...{ inert: isAddingGiftCardsDisabled ? '' : undefined }}
              >
                {availableGiftCards.map((card, index) => {
                  const isCurrentlySelected =
                    card?.code === currentlyAddedGiftCard?.code
                  return (
                    <Flex
                      key={index}
                      justifyContent="center"
                      alignItems="center"
                      backgroundColor="foreground"
                      borderRadius="medium"
                      inset={{
                        horizontal: 'x1',
                        top: 'x1',
                        bottom: '0.25rem',
                      }}
                      onClick={() => {
                        if (card) {
                          setMobileSheetHeight('80vh')
                          setSelectedGiftCard(card)
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.08)',
                        position: 'relative',
                        backgroundColor: isCurrentlySelected
                          ? 'mediumseagreen'
                          : undefined,
                      }}
                    >
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {isCurrentlySelected && (
                          <div
                            style={{
                              position: 'absolute',
                              display: 'flex',
                              padding: '4px 8px',
                              backgroundColor: '#f5f6f7',
                              borderRadius: '8px',
                              columnGap: '4px',
                              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.08)',
                            }}
                            onClick={e => {
                              e.stopPropagation()
                              if (currentlyAddedGiftCard)
                                onRemove(currentlyAddedGiftCard)
                            }}
                          >
                            <Text
                              type="caption"
                              color="danger"
                              content="Remove"
                            />
                            <Icon
                              name="trash"
                              size={20}
                              color="hsl(0, 84%, 67%)"
                            />
                          </div>
                        )}
                        <div
                          style={{
                            height: '100px',
                            width: '100%',
                            backgroundImage: `url('${card?.image}')`,
                            backgroundSize: 'cover',
                            borderRadius: '12px',
                          }}
                        />
                        <Flex
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          style={{ marginTop: '4px', padding: '0 0.25rem' }}
                        >
                          <Text
                            type="body"
                            content={card?.title}
                            style={{
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              color: '#404040',
                              overflow: 'hidden',
                              whiteSpace: 'pre',
                              flexShrink: 1,
                              maxWidth: '50%',
                              textOverflow: 'ellipsis',
                            }}
                          />
                          <div
                            style={{
                              backgroundColor: '#c7e8c2',
                              borderRadius: '50px',
                              padding: '0 8px',
                              height: '100%',
                            }}
                          >
                            <Text
                              type="body"
                              content={`Up to $${card.maximumAmount ? parseInt(card.maximumAmount) : 10}`}
                              style={{
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                color: '#348d26',
                                lineHeight: '1.25rem',
                              }}
                            />
                          </div>
                        </Flex>
                      </div>
                    </Flex>
                  )
                })}
              </motion.div>
            )}
          </>
        )}
      </AnimatePresence>
      {isAddingGiftCardsDisabled && (
        <div
          style={{
            position: 'absolute',
            marginTop: '64px',
            inset: 0,
            backgroundColor: '#00000077',
            backdropFilter: 'blur(10px)',

            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '22px',
          }}
        >
          <Icon name={'info'} color="white" size={64} />
          <Text
            type={'body'}
            alignment="center"
            color="inverseHeading"
            weight="bold"
          >
            Digital Gift Cards Currently Unavailable
          </Text>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',

              margin: '0 1em',
            }}
          >
            <Text
              type={'caption'}
              alignment="center"
              color="inverseBody"
              weight="bold"
            >
              We are undergoing maintenance on our digital gift cards, please
              check back later.
            </Text>
          </div>
        </div>
      )}
    </>
  )
}

export default GiftCardCatalog
